import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { useLogin } from '@/hooks/useAuth';
import { useStaffLogin } from '@/hooks/useStaffAuth';
import { useAssistantLogin } from '@/hooks/useAssistantAuth';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LanguageSelector } from '@/components/layout/LanguageSelector';
import { Logo } from '@/components/common/Logo';

interface LoginFormProps {
  onSuccess?: () => void;
  restaurantId?: string; // Restaurant ID from URL for staff/assistant login
  mode?: 'admin' | 'staff' | 'assistant'; // Login mode
  isStaffLogin?: boolean; // DEPRECATED: Use mode='staff' instead
}

export function LoginForm({ onSuccess, restaurantId, mode, isStaffLogin = false }: LoginFormProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState(''); // Can be email or username
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const loginMutation = useLogin();
  const staffLoginMutation = useStaffLogin();
  const assistantLoginMutation = useAssistantLogin();

  // Determine actual mode (support backward compatibility)
  const actualMode = mode || (isStaffLogin ? 'staff' : 'admin');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    // Basic validation
    const newErrors: Record<string, string> = {};

    if (!identifier) {
      newErrors['identifier'] = t('validation.required');
    }

    if (!password) {
      newErrors['password'] = t('validation.required');
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      if (actualMode === 'staff') {
        // Staff login - username only
        if (!restaurantId) {
          setErrors({
            form: 'Invalid staff login link. Please use the link provided by your admin.',
          });
          return;
        }

        await staffLoginMutation.mutateAsync({ username: identifier, password, restaurantId });

        // Get staff data from localStorage to determine role
        const staffData = localStorage.getItem('digimataam_staff_auth');
        if (staffData) {
          const staff = JSON.parse(staffData);
          if (staff.role === 'server') {
            navigate({ to: '/staff-server' });
          } else if (staff.role === 'kitchen') {
            navigate({ to: '/staff-kitchen' });
          }
        }

        onSuccess?.();
      } else if (actualMode === 'assistant') {
        // Assistant login - username only
        if (!restaurantId) {
          setErrors({
            form: 'Invalid assistant login link. Please use the link provided by your admin.',
          });
          return;
        }

        await assistantLoginMutation.mutateAsync({ username: identifier, password, restaurantId });

        // Navigation is handled by parent component (AssistantLogin)
        onSuccess?.();
      } else {
        // Admin login - email only
        const isEmail = identifier.includes('@');

        if (!isEmail) {
          setErrors({
            form: 'Please use your email address to log in as admin.',
          });
          return;
        }

        await loginMutation.mutateAsync({ email: identifier, password });

        // Fetch user profile to check if first login
        const { data: { user: authUser } } = await supabase.auth.getUser();
        if (authUser) {
          const { data: profile } = await supabase
            .from('users')
            .select('is_first_login')
            .eq('id', authUser.id)
            .single();

          if (profile?.is_first_login) {
            // Mark first login as complete
            await supabase.rpc('mark_first_login_complete', { p_user_id: authUser.id });

            // Redirect to welcome page
            navigate({ to: '/welcome' });
          }
        }

        onSuccess?.();
        // Admin will stay on current route or be redirected by ProtectedRoute if not first login
      }
    } catch (error) {
      console.error('Login error:', error);
      let errorMessage = t('auth.loginError');

      if (error && typeof error === 'object' && 'message' in error) {
        errorMessage = (error as { message: string }).message;
      }

      setErrors({
        form: errorMessage,
      });
    }
  };

  return (
    <Card className="w-full max-w-md shadow-lg">
      <CardHeader className="space-y-4">
        <div className="flex items-center justify-center mt-10">
          <Logo className="h-40 w-40 sm:h-32 sm:w-32 md:h-40 md:w-40" />
        </div>
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <CardTitle className="text-center sm:text-left">
              {actualMode === 'staff'
                ? t('staff.staffLogin') || 'Staff Login'
                : actualMode === 'assistant'
                  ? t('assistant.assistantLogin') || 'Assistant Login'
                  : t('auth.login')}
            </CardTitle>
          </div>
          <div className="flex-shrink-0 ml-2">
            <LanguageSelector />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="identifier">
              {actualMode === 'admin' ? t('auth.email') : t('staff.username') || 'Username'}
            </Label>
            <Input
              id="identifier"
              type="text"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              disabled={loginMutation.isPending}
              placeholder={actualMode === 'admin' ? 'admin@example.com' : 'username'}
              autoComplete={actualMode === 'admin' ? 'email' : 'username'}
            />
            {errors['identifier'] && (
              <p className="text-sm text-destructive">{errors['identifier']}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">{t('auth.password')}</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loginMutation.isPending}
              autoComplete="current-password"
            />
            {errors['password'] && <p className="text-sm text-destructive">{errors['password']}</p>}
          </div>

          {errors['form'] && (
            <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md">
              {errors['form']}
            </div>
          )}

          <Button type="submit" className="w-full" disabled={loginMutation.isPending}>
            {loginMutation.isPending ? t('common.loading') : t('auth.login')}
          </Button>
        </form>

        {!isStaffLogin && (
          <div className="mt-6 pt-6 border-t border-border">
            <p className="text-sm text-center text-muted-foreground mb-3">
              {t('auth.noAccount') || "Don't have an account?"}
            </p>
            <a
              href="https://digimataam.com"
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full text-center text-sm font-medium text-primary hover:text-primary/80 transition-colors underline"
            >
              {t('auth.visitLanding') || 'Visit digimataam.com to get started'}
            </a>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
